import {zodResolver} from '@hookform/resolvers/zod';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {FormSkeleton} from '@/components/ui/skeleton';
import {Textarea} from '@/components/ui/textarea';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useGetWorkTimeEntriesByDateRangeQuery,
  useGetWorkTimeEntryQuery,
  useRejectWorkTimeEntryMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {RejectWorkTimeEntryFormValues} from '../utils/workTimeFormInputs';
import {rejectWorkTimeEntryFormSchema} from '../utils/workTimeFormInputs';

export type RejectWorkTimeEntryDialogProps = DialogProps;

const RejectWorkTimeEntryDialog = ({
  onOpenChange,
  ...props
}: RejectWorkTimeEntryDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('rejectWorkTimeEntry') || '';
  const entryQuery = useGetWorkTimeEntryQuery(
    graphqlClient,
    {id},
    {enabled: !!id}
  );

  const rejectWorkTimeEntry = useRejectWorkTimeEntryMutation(graphqlClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useGetWorkTimeEntriesByDateRangeQuery.getKey(),
      });
    },
  });

  const form = useForm<RejectWorkTimeEntryFormValues>({
    resolver: zodResolver(rejectWorkTimeEntryFormSchema),
    defaultValues: {note: ''},
  });

  useEffect(() => {
    const entry = entryQuery.data?.getWorkTimeEntry;
    if (!entry) return;

    form.reset({note: entry.note || ''});
  }, [entryQuery.data, form]);

  const handleClose = (open: boolean) => {
    form.reset();
    onOpenChange?.(open);
  };

  const handleSubmit = async (values: RejectWorkTimeEntryFormValues) => {
    try {
      await rejectWorkTimeEntry.mutateAsync({
        input: {id, note: values.note},
      });
      toast({
        title: t('Shift rejected'),
        variant: 'success',
      });
      handleClose(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={handleClose}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Reject shift')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              {entryQuery.isLoading && <FormSkeleton size={1} />}
              {!entryQuery.isLoading && (
                <>
                  <FormRequiredLegend />

                  <FormField
                    control={form.control}
                    name="note"
                    render={({field}) => (
                      <FormItem>
                        <FormLabel required>{t('Rejection reason')}</FormLabel>
                        <FormControl>
                          <Textarea {...field} autoFocus />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleClose(false)}
              >
                {t('Close')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  entryQuery.isLoading ||
                  rejectWorkTimeEntry.isPending ||
                  !entryQuery.data?.getWorkTimeEntry
                }
              >
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default RejectWorkTimeEntryDialog;
